import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Calendar as CalendarIcon } from "lucide-react";
import { Button } from "@shared/components/ui/button";
import Calendar from "@features/booking/components/Calendar";
import { BookingBreadcrumbs } from "@features/booking/components/BookingBreadcrumbs";
import { BookingPageLayout } from "@features/booking/layouts";
import { format } from "date-fns";
import { useLanguage } from "@core/i18n";
import { useBooking } from "@features/booking/state";
import { useCalendarAvailability } from "@features/booking/hooks/useCalendarAvailability";
import { applyCalendarIntentOverride, buildDateSelectionUpdate, buildServiceResetForDateSelection } from "@features/booking/utils/availability";
import { useRestaurant } from "@core/tenant/TenantProvider";
import { useAuth } from "@core/auth/AuthProvider";
import { useBookingFlow } from "@features/booking/flow";
import type { ServiceType } from "@features/booking/state/types";

const FullCalendarPage = () => {
  const { t } = useTranslation('booking');
  const { dateLocale } = useLanguage();
  const navigate = useNavigate();
  const booking = useBooking();
  const { diners, selectedDate, setSelectedDate, meitreNetworkMenuId, updateBooking, specialEvent } = booking;
  const restaurant = useRestaurant();
  const { authState } = useAuth();
  const { activeSteps, goToStep } = useBookingFlow();

  useEffect(() => {
    if (specialEvent) {
      navigate('/select-date', { replace: true });
    }
  }, [specialEvent, navigate]);

  const [selectedCalendarDate, setSelectedCalendarDate] = useState<Date | undefined>(
    selectedDate ? new Date(selectedDate + 'T12:00:00') : undefined
  );

  // FullCalendar is an alternate view of the DATE step
  const dateStepIndex = useMemo(
    () => activeSteps.findIndex(s => s.id === 'DATE'),
    [activeSteps],
  );

  const progress = useMemo(() => {
    if (activeSteps.length <= 1) return 100;
    if (dateStepIndex < 0) return 0;
    return Math.round(((dateStepIndex + 1) / activeSteps.length) * 100);
  }, [dateStepIndex, activeSteps.length]);

  // Get valid service types from restaurant
  const serviceTypes = (restaurant.serviceTypes || []) as ServiceType[];

  // Fetch calendar availability (will use cached data from SelectDinersPage)
  const {
    mergedCalendar,
    isLoading: isCalendarLoading,
    getDateAvailability,
  } = useCalendarAvailability({
    restaurantId: restaurant.id,
    partySize: diners,
    serviceTypes,
    enabled: diners > 0,
    meitreNetworkMenuId,
    preSelectedMenuId: booking.preSelectedMenuId,
    hasPreferredAccess: !!authState?.preferred,
  });

  // Always show non-closed days as available so users can select any open date
  // (real availability is shown in the time slots step). Matches legacy behavior.
  const calendarData = useMemo(
    () => !meitreNetworkMenuId && !booking.preSelectedMenuId
      ? applyCalendarIntentOverride(mergedCalendar)
      : mergedCalendar,
    [mergedCalendar, meitreNetworkMenuId, booking.preSelectedMenuId],
  );

  // Sync local state with booking state when it changes
  useEffect(() => {
    if (selectedDate) {
      setSelectedCalendarDate(new Date(selectedDate + 'T12:00:00'));
    }
  }, [selectedDate]);

  const handleBack = () => {
    goToStep('DATE');
  };

  const handleCalendarChange = (date: Date) => {
    setSelectedCalendarDate(date);
    setSelectedDate(format(date, 'yyyy-MM-dd'));
  };

  // Deferred navigation: updateBooking is async (React batches state updates),
  // so activeSteps won't reflect isEarlyAccessFlow until the next render.
  // We set a flag here and navigate via useEffect after the state commits.
  const [pendingContinue, setPendingContinue] = useState(false);

  useEffect(() => {
    if (!pendingContinue) return;
    setPendingContinue(false);
    // activeSteps is now fresh (includes EARLY_ACCESS if isEarlyAccessFlow was set)
    const nextStep = dateStepIndex >= 0 && dateStepIndex < activeSteps.length - 1
      ? activeSteps[dateStepIndex + 1]
      : null;
    if (nextStep) {
      goToStep(nextStep.id);
    }
  }, [pendingContinue, activeSteps, dateStepIndex, goToStep]);

  const handleContinue = () => {
    if (!selectedCalendarDate) return;
    // Detect early access + capture intent + save available services
    const dateKey = format(selectedCalendarDate, 'yyyy-MM-dd');
    const dayAvail = getDateAvailability(dateKey);

    const { updates, availableServices, singleService } = buildDateSelectionUpdate(dayAvail, serviceTypes);
    const serviceReset = buildServiceResetForDateSelection({
      currentService: booking.service,
      availableServiceTypes: availableServices,
      eventServiceTypes: specialEvent?.serviceTypes,
    });
    updateBooking({
      ...updates,
      timeSlots: null,
      ...(singleService ? { service: singleService } : serviceReset),
    });
    // Defer navigation to next render so activeSteps reflects the new isEarlyAccessFlow
    setPendingContinue(true);
  };

  const dateText = selectedCalendarDate
    ? format(selectedCalendarDate, "EEEE, MMMM d, yyyy", { locale: dateLocale })
    : t('DATE_SELECT');

  return (
    <BookingPageLayout
      title={t('STRING_SELECT')}
      subtitle={t('STRING_NO_RESULTS_DATE')}
      breadcrumbs={<BookingBreadcrumbs show={['DINERS']} />}
      onBack={handleBack}
      progress={progress}
    >
      <div className="w-full px-0 sm:px-6 mb-10">
        <div className="max-w-md mx-auto">
          <Calendar
            value={selectedCalendarDate}
            onChange={handleCalendarChange}
            availability={calendarData}
            isLoading={isCalendarLoading}
            locale={dateLocale}
          />
        </div>
      </div>

      <div className="w-full px-6">
        {selectedCalendarDate && (
          <div className="text-center mb-6">
            <p className="text-sm text-foreground/50">{t('DATE_SELECTED')}</p>
            <p className="text-lg font-medium text-brand">{dateText}</p>
          </div>
        )}
        <Button
          className="bg-brand text-brand-foreground font-medium py-6 px-8 rounded-xl w-full hover:bg-brand-dark shadow-md flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={handleContinue}
          disabled={!selectedCalendarDate}
        >
          <CalendarIcon size={18} />
          {t('DATE_CONTINUE')}
        </Button>
      </div>
    </BookingPageLayout>
  );
};

export default FullCalendarPage;
